import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import { isFreeToolRecordingUrl } from './freeToolPrivacy';

type LogRocketTraitValue = string | number | boolean;
type LogRocketTraits = Record<string, LogRocketTraitValue>;
type LogRocketTrackValue = string | number | boolean | string[] | number[] | boolean[];
type LogRocketTrackProps = Record<string, LogRocketTrackValue>;

const isBrowser = () => typeof window !== 'undefined';
const getAppId = () => process.env.NEXT_PUBLIC_LOGROCKET_APP_ID || '';

let initialized = false;

const isPrimitive = (v: unknown): v is string | number | boolean =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const coerceTraits = (traits: Record<string, unknown>): LogRocketTraits => {
  const out: LogRocketTraits = {};
  Object.entries(traits).forEach(([key, value]) => {
    if (isPrimitive(value)) {
      out[key] = value;
    }
  });
  return out;
};

// LogRocket.track accepts primitives or arrays-of-same-primitive (string[],
// number[], or boolean[]). Mixed-type arrays and objects get JSON-stringified
// so we keep the data rather than silently dropping it.
const coerceTrackProps = (props: Record<string, unknown>): LogRocketTrackProps => {
  const out: LogRocketTrackProps = {};
  Object.entries(props).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (isPrimitive(value)) {
      out[key] = value;
      return;
    }
    if (Array.isArray(value) && value.length > 0) {
      const firstType = typeof value[0];
      if (
        (firstType === 'string' || firstType === 'number' || firstType === 'boolean') &&
        value.every((v) => typeof v === firstType)
      ) {
        out[key] = value as string[] | number[] | boolean[];
        return;
      }
    }
    try {
      out[key] = JSON.stringify(value);
    } catch {
      // unserializable (circular ref, etc.) - skip
    }
  });
  return out;
};

// LogRocket defaults to storing session state on a third-party domain, which
// incognito / private browsing and strict tracking-prevention modes block.
// Passing rootHostname tells LogRocket to use first-party cookies on the app's
// own domain so sessions still record in those contexts.
const resolveRootHostname = (): string | undefined => {
  const explicit = process.env.NEXT_PUBLIC_LOGROCKET_ROOT_HOSTNAME;
  if (explicit) return explicit;
  if (!isBrowser()) return undefined;

  const host = window.location.hostname;
  // Skip transformation for localhost, IP addresses, and single-segment hosts
  if (!host.includes('.') || /^[\d.]+$/.test(host)) return host;

  // app.quattr.com -> quattr.com  (last two labels)
  const parts = host.split('.');
  return parts.slice(-2).join('.');
};

/**
 * Initialize the LogRocket SDK. Safe to call multiple times - subsequent calls
 * no-op. No-ops in SSR contexts and when NEXT_PUBLIC_LOGROCKET_APP_ID is unset
 * (lets local dev run without a LogRocket project).
 */
export const initLogRocket = (): void => {
  if (initialized || !isBrowser()) return;
  const appId = getAppId();
  if (!appId) return;

  const rootHostname = resolveRootHostname();
  // mergeIframes: the WordPress preview boots LogRocket inside its (same-origin) frame so the
  // previewed page is part of this session's recording.
  LogRocket.init(appId, {
    mergeIframes: true,
    ...(rootHostname ? { rootHostname } : {}),
    // Returning null excludes the request and its response from replay capture.
    // Generation and signup still use the original requests; only recording is suppressed.
    network: {
      requestSanitizer: (request) => (isFreeToolRecordingUrl(request.url, window.location.pathname) ? null : request),
    },
  });
  // logrocket-react's type signature is outdated (declares zero args) but the package requires LogRocket at runtime.
  (setupLogRocketReact as unknown as (lr: typeof LogRocket) => void)(LogRocket);
  initialized = true;
};

/** True once initLogRocket has successfully run. Useful for downstream guards. */
export const isLogRocketInitialized = (): boolean => initialized;
export const getLogRocketAppId = (): string => getAppId();
export const getLogRocketRootHostname = (): string => resolveRootHostname() || '';

/**
 * Identify the current user in LogRocket. Trait values are coerced to LogRocket's
 * accepted types (string/number/boolean); non-primitives are silently dropped.
 * No-op before init or when userId is empty.
 */
export const identifyLogRocketUser = (userId: string, traits: Record<string, unknown> = {}): void => {
  if (!initialized || !userId) return;
  const coerced = coerceTraits(traits);
  lastIdentity = { userId, traits: coerced };
  LogRocket.identify(userId, coerced);
};

let lastIdentity: { userId: string; traits: LogRocketTraits } | null = null;

/**
 * The user identity last sent to LogRocket, so recorded preview frames can identify their own
 * sessions as the same user (frame sessions are otherwise anonymous and unfindable by user).
 */
export const getLogRocketIdentity = (): { userId: string; traits: LogRocketTraits } | null => lastIdentity;

/**
 * Forward a custom analytics event into the LogRocket session timeline so it shows
 * up alongside auto-captured clicks/network events when watching replays.
 * Properties are coerced to LogRocket's accepted shape: primitives and same-typed
 * primitive arrays pass through; nested objects and mixed arrays get JSON-stringified.
 */
export const trackLogRocketEvent = (eventName: string, properties: Record<string, unknown> = {}): void => {
  if (!initialized || !eventName) return;
  LogRocket.track(eventName, coerceTrackProps(properties));
};

/**
 * Get the current session's LogRocket replay URL via the SDK callback. We use this
 * to stamp Amplitude events with `logRocketSessionURL` so analytics dashboards can
 * jump straight into the replay.
 */
export const getLogRocketSessionURL = (cb: (url: string) => void): void => {
  if (!initialized) return;
  LogRocket.getSessionURL(cb);
};
